import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Chip, Dialog, DialogActions, DialogContent, IconButton, InputAdornment, LinearProgress, Skeleton, Tab, Tabs, TextField, Typography,
} from '@mui/material';
import { MdCameraAlt, MdClose, MdComputer, MdEdit, MdLock, MdLogout, MdPhoneIphone, MdVisibility, MdVisibilityOff } from 'react-icons/md';
import { deleteData, getData, postData, postFileUpload } from '../../Webservice/Webservice';

const FONT = 'Mulish, sans-serif';

const authToken = () => {
  const raw = sessionStorage.getItem('token');
  return raw ? `Bearer ${raw}` : '';
};
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—');
const fmtTime = (d) => (d ? new Date(d).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—');
const pretty = (s) => (s ? String(s).replace(/_/g, ' ') : '—');
const browser = (ua) => {
  if (!ua) return 'Unknown device';
  const b = /edg/i.test(ua) ? 'Edge' : /firefox/i.test(ua) ? 'Firefox' : /chrome/i.test(ua) ? 'Chrome' : /safari/i.test(ua) ? 'Safari' : 'Browser';
  const o = /windows/i.test(ua) ? 'Windows' : /android/i.test(ua) ? 'Android' : /iphone|ipad|ios/i.test(ua) ? 'iOS' : /mac os/i.test(ua) ? 'macOS' : /linux/i.test(ua) ? 'Linux' : '';
  return o ? `${b} on ${o}` : b;
};
const isMobile = (ua) => /mobile|android|iphone/i.test(ua || '');
const strength = (p) => {
  let s = 0;
  if (p.length >= 8) s++;
  if (p.length >= 12) s++;
  if (/[A-Z]/.test(p) && /[a-z]/.test(p)) s++;
  if (/\d/.test(p)) s++;
  if (/[^A-Za-z0-9]/.test(p)) s++;
  return Math.min(s, 4);
};
const STRENGTH = [['Too weak', '#ef4444'], ['Weak', '#f97316'], ['Okay', '#eab308'], ['Good', '#22c55e'], ['Strong', '#15803d']];

const yellowBtn = { textTransform: 'none', fontWeight: 800, background: 'rgb(255 209 2)', color: 'black', borderRadius: '10px', px: 3, boxShadow: '0 4px 12px rgba(255, 209, 2, 0.3)', '&:hover': { background: 'rgb(255 190 0)' } };

const Card = ({ title, accent, children, right }) => (
  <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderTop: `4px solid ${accent}`, borderRadius: '14px', p: 2.5 }}>
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1, gap: 1, flexWrap: 'wrap' }}>
      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 15 }}>{title}</Typography>
      {right}
    </Box>
    {children}
  </Box>
);
const Row = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.1, borderBottom: '1px dashed #e2e8f0' }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 13.5, fontWeight: 700, color: '#0f172a', textAlign: 'right', wordBreak: 'break-word' }}>{value || '—'}</Typography>
  </Box>
);
const Stat = ({ label, value, color }) => (
  <Box sx={{ flex: 1, minWidth: 150, p: 2, borderRadius: '14px', background: `${color}12`, border: `1px solid ${color}33` }}>
    <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#64748b', fontWeight: 700 }}>{label}</Typography>
    <Typography sx={{ fontFamily: FONT, fontSize: 18, fontWeight: 800, color: '#0f172a' }}>{value}</Typography>
  </Box>
);

function Profile() {
  const [user, setUser] = useState(null);
  const [recent, setRecent] = useState([]);
  const [failed, setFailed] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(0);
  const [notice, setNotice] = useState('');

  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(true);
  const [revokeTarget, setRevokeTarget] = useState(null); // session | 'others'
  const [revoking, setRevoking] = useState(false);

  // edit profile
  const [editOpen, setEditOpen] = useState(false);
  const [form, setForm] = useState({ firstName: '', lastName: '', email: '', phoneNumber: '' });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  // password
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ oldPassword: '', newPassword: '', confirmPassword: '' });
  const [show, setShow] = useState({ old: false, next: false, confirm: false });
  const [pwErrors, setPwErrors] = useState({});
  const [pwError, setPwError] = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const stored = () => {
    try { return JSON.parse(sessionStorage.getItem('user') || 'null'); } catch { return null; }
  };

  const loadMe = useCallback(async () => {
    setLoading(true);
    const local = stored();
    try {
      const res = await getData('profile/me', null, authToken(), false);
      if (res?.user) {
        setUser(res.user);
        setRecent(res.recentLogins || []);
        setFailed(res.failedAttempts7d || 0);
        return;
      }
    } catch { /* fall back to what login saved */ }
    if (local) setUser(local); else setError('No user data found. Please log in again.');
  }, []);
  useEffect(() => { loadMe().finally(() => setLoading(false)); }, [loadMe]);

  const loadSessions = useCallback(async () => {
    setSessionsLoading(true);
    try {
      const res = await getData('auth/sessions', null, authToken(), false);
      setSessions(res?.sessions || []);
    } catch { setSessions([]); } finally { setSessionsLoading(false); }
  }, []);
  useEffect(() => { loadSessions(); }, [loadSessions]);

  const avatarUrl = user?.image || user?.avatar || user?.profileImage || '';
  const fullName = useMemo(() => `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || user?.name || 'User', [user]);
  const roleName = user?.roleName || (typeof user?.role === 'string' ? user.role : '') || '';
  const status = String(user?.status || 'active').toLowerCase();

  // ---- edit profile ----
  const openEdit = () => {
    setForm({ firstName: user.firstName || '', lastName: user.lastName || '', email: user.email || '', phoneNumber: user.phoneNumber || '' });
    setFile(null);
    setPreview(avatarUrl || null);
    setErrors({});
    setFormError('');
    setEditOpen(true);
  };
  const pickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith('image/')) return setFormError('Choose an image file.');
    if (f.size > 5 * 1024 * 1024) return setFormError('The image must be under 5 MB.');
    setFormError('');
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };
  const saveProfile = async (ev) => {
    ev.preventDefault();
    setFormError('');
    const e = {};
    if (form.firstName.trim().length < 2) e.firstName = 'At least 2 characters';
    if (form.lastName.trim().length < 2) e.lastName = 'At least 2 characters';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) e.email = 'Enter a valid email';
    if (form.phoneNumber && !/^\d{10}$/.test(form.phoneNumber.replace(/\s/g, ''))) e.phoneNumber = 'Enter 10 digits';
    setErrors(e);
    if (Object.keys(e).length) return;
    setSaving(true);
    try {
      const body = new FormData();
      body.append('firstName', form.firstName.trim());
      body.append('lastName', form.lastName.trim());
      body.append('email', form.email.trim());
      if (form.phoneNumber) body.append('phoneNumber', form.phoneNumber.replace(/\s/g, ''));
      if (file) body.append('image', file);
      const res = await postFileUpload('profile/update', body, authToken());
      const saved = res?.data || {};
      const merged = { ...user, ...saved };
      setUser(merged);
      const session = { ...(stored() || {}), firstName: merged.firstName, lastName: merged.lastName, email: merged.email, phoneNumber: merged.phoneNumber, image: merged.image, avatar: merged.image };
      sessionStorage.setItem('user', JSON.stringify(session));
      window.dispatchEvent(new Event('userUpdate'));
      setEditOpen(false);
      setNotice('Profile updated.');
    } catch (err) {
      setFormError(err?.message || 'Could not update your profile.');
    } finally {
      setSaving(false);
    }
  };

  // ---- password ----
  const openPw = () => {
    setPw({ oldPassword: '', newPassword: '', confirmPassword: '' });
    setShow({ old: false, next: false, confirm: false });
    setPwErrors({});
    setPwError('');
    setPwOpen(true);
  };
  const savePw = async (ev) => {
    ev.preventDefault();
    setPwError('');
    const e = {};
    if (!pw.oldPassword) e.oldPassword = 'Enter your current password';
    if (pw.newPassword.length < 8) e.newPassword = 'At least 8 characters';
    else if (pw.newPassword === pw.oldPassword) e.newPassword = 'Choose a different password';
    if (pw.confirmPassword !== pw.newPassword) e.confirmPassword = 'Passwords do not match';
    setPwErrors(e);
    if (Object.keys(e).length) return;
    setPwSaving(true);
    try {
      await postData('profile/update/password', { oldPassword: pw.oldPassword, newPassword: pw.newPassword }, authToken(), true);
      setPwOpen(false);
      setNotice('Password changed.');
    } catch (err) {
      setPwError(err?.message || 'Could not change the password.');
    } finally {
      setPwSaving(false);
    }
  };

  // ---- sessions ----
  const confirmRevoke = async () => {
    setRevoking(true);
    try {
      if (revokeTarget === 'others') {
        await postData('auth/sessions/revoke-others', {}, authToken(), true);
        setNotice('Signed out of every other device.');
      } else {
        await deleteData(`auth/sessions/${revokeTarget.id}`, authToken());
        if (revokeTarget.current) {
          sessionStorage.clear();
          window.location.replace('/');
          return;
        }
        setNotice('That device was signed out.');
      }
      setRevokeTarget(null);
      loadSessions();
    } catch { /* the service shows the error */ } finally {
      setRevoking(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: { xs: 1.5, md: 3 } }}>
        <Skeleton variant="rounded" height={170} sx={{ borderRadius: '18px', mb: 2 }} />
        <Skeleton variant="rounded" height={300} sx={{ borderRadius: '14px' }} />
      </Box>
    );
  }
  if (error || !user) {
    return <Box sx={{ p: 3 }}><Alert severity="error" sx={{ maxWidth: 600, mx: 'auto' }}>{error || 'No user data available. Please log in again.'}</Alert></Box>;
  }

  const pwScore = strength(pw.newPassword);
  const eye = (key) => ({
    endAdornment: (
      <InputAdornment position="end">
        <IconButton size="small" onClick={() => setShow((s) => ({ ...s, [key]: !s[key] }))} edge="end">{show[key] ? <MdVisibilityOff size={18} /> : <MdVisibility size={18} />}</IconButton>
      </InputAdornment>
    ),
  });

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, fontFamily: FONT, maxWidth: 1100, mx: 'auto' }}>
      {notice && <Alert severity="success" onClose={() => setNotice('')} sx={{ mb: 2 }}>{notice}</Alert>}

      <Box sx={{ borderRadius: '18px', overflow: 'hidden', border: '1px solid #e2e8f0', background: '#fff', mb: 2.5 }}>
        <Box sx={{ height: 96, background: 'linear-gradient(135deg,#6366f1,#4338ca)' }} />
        <Box sx={{ px: { xs: 2, md: 3.5 }, pb: 2.5, display: 'flex', gap: 2.5, alignItems: 'flex-end', flexWrap: 'wrap', mt: '-48px' }}>
          <Avatar src={avatarUrl || undefined} alt={fullName} sx={{ width: 104, height: 104, border: '4px solid #fff', boxShadow: '0 4px 14px rgba(0,0,0,.15)', fontSize: 38, fontWeight: 800, bgcolor: '#e0e7ff', color: '#3730a3' }}>
            {(fullName[0] || 'U').toUpperCase()}
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 220, pt: { xs: 0, md: 6 } }}>
            <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 24, color: '#0f172a' }}>{fullName}</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
              {roleName && <Chip size="small" label={pretty(roleName)} sx={{ fontFamily: FONT, fontWeight: 700, textTransform: 'capitalize', background: '#e0e7ff', color: '#3730a3' }} />}
              <Chip size="small" label={status === 'active' ? 'Active' : 'Inactive'} sx={{ fontFamily: FONT, fontWeight: 700, background: status === 'active' ? '#dcfce7' : '#f1f5f9', color: status === 'active' ? '#166534' : '#475569' }} />
              <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>{user.email}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', pt: { xs: 0, md: 6 } }}>
            <Button startIcon={<MdEdit />} variant="contained" onClick={openEdit} sx={yellowBtn}>Edit profile</Button>
            <Button startIcon={<MdLock />} variant="outlined" onClick={openPw} sx={{ textTransform: 'none', fontWeight: 800, borderRadius: '10px', color: '#0f172a', borderColor: '#cbd5e1' }}>Change password</Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2.5 }}>
        <Stat label="Member since" value={fmtDate(user.createdAt)} color="#6366f1" />
        <Stat label="Last sign-in" value={recent[0] ? fmtTime(recent[0].createdAt) : '—'} color="#0ea5e9" />
        <Stat label="Signed-in devices" value={sessionsLoading ? '…' : sessions.length} color="#22c55e" />
        <Stat label="Failed sign-ins (7 days)" value={failed} color={failed ? '#ef4444' : '#94a3b8'} />
      </Box>

      <Box sx={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '14px', overflow: 'hidden' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: '1px solid #e2e8f0', px: 1.5, '& .MuiTab-root': { textTransform: 'none', fontFamily: FONT, fontWeight: 700 } }}>
          <Tab label="Details" />
          <Tab label={`Devices (${sessions.length})`} />
          <Tab label="Recent sign-ins" />
        </Tabs>

        <Box sx={{ p: 2.5 }}>
          {tab === 0 && (
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
              <Card title="Personal" accent="#6366f1">
                <Row label="First name" value={user.firstName} />
                <Row label="Last name" value={user.lastName} />
                <Row label="Email" value={user.email} />
                <Row label="Phone" value={user.phoneNumber} />
              </Card>
              <Card title="Account" accent="#0ea5e9">
                <Row label="Role" value={<span style={{ textTransform: 'capitalize' }}>{pretty(roleName)}</span>} />
                <Row label="Employee ID" value={user.empId} />
                <Row label="Status" value={status === 'active' ? 'Active' : 'Inactive'} />
                <Row label="Joined" value={fmtDate(user.createdAt)} />
              </Card>
            </Box>
          )}

          {tab === 1 && (
            <Card title="Where you are signed in" accent="#22c55e" right={sessions.length > 1 && (
              <Button size="small" startIcon={<MdLogout />} onClick={() => setRevokeTarget('others')} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626' }}>Sign out all other devices</Button>
            )}>
              {sessionsLoading && <Skeleton variant="rounded" height={70} />}
              {!sessionsLoading && sessions.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No active sessions found.</Typography>}
              {sessions.map((s) => (
                <Box key={s.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.4, borderBottom: '1px dashed #e2e8f0', flexWrap: 'wrap' }}>
                  <Box sx={{ background: '#f1f5f9', borderRadius: '10px', p: 1, display: 'flex', color: '#475569' }}>{isMobile(s.userAgent) ? <MdPhoneIphone size={20} /> : <MdComputer size={20} />}</Box>
                  <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 13.5 }}>{browser(s.userAgent)}</Typography>
                      {s.current && <Chip size="small" label="This device" sx={{ height: 20, fontWeight: 700, background: '#dcfce7', color: '#166534' }} />}
                    </Box>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{s.ip ? `${s.ip} · ` : ''}Last active {fmtTime(s.lastActiveAt)}</Typography>
                  </Box>
                  <Button size="small" onClick={() => setRevokeTarget(s)} sx={{ textTransform: 'none', fontWeight: 700, color: '#dc2626', border: '1px solid #fecaca', borderRadius: '8px' }}>{s.current ? 'Sign out' : 'Revoke'}</Button>
                </Box>
              ))}
            </Card>
          )}

          {tab === 2 && (
            <Card title="Your last successful sign-ins" accent="#f59e0b">
              {recent.length === 0 && <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#64748b' }}>No sign-ins recorded yet.</Typography>}
              {recent.map((r, i) => (
                <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 1.2, borderBottom: '1px dashed #e2e8f0', flexWrap: 'wrap' }}>
                  <Box>
                    <Typography sx={{ fontFamily: FONT, fontWeight: 700, fontSize: 13.5 }}>{fmtTime(r.createdAt)}</Typography>
                    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, color: '#64748b' }}>{browser(r.userAgent)}</Typography>
                  </Box>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: 12.5, color: '#475569' }}>{r.ipAddress || '—'}</Typography>
                </Box>
              ))}
              <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', mt: 1.5 }}>Don’t recognise one? Change your password and sign out the other devices.</Typography>
            </Card>
          )}
        </Box>
      </Box>

      {/* edit profile */}
      <Dialog open={editOpen} onClose={() => !saving && setEditOpen(false)} maxWidth="sm" fullWidth scroll="paper" PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2.25, borderBottom: '3px solid rgb(255 209 2)', background: '#fff' }}>
          <Typography sx={{ flex: 1, fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>Edit profile</Typography>
          <IconButton onClick={() => setEditOpen(false)} disabled={saving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={saveProfile} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar src={preview || undefined} sx={{ width: 84, height: 84, fontSize: 30, fontWeight: 800, bgcolor: '#e0e7ff', color: '#3730a3' }}>{(fullName[0] || 'U').toUpperCase()}</Avatar>
              <Box>
                <Button component="label" startIcon={<MdCameraAlt />} sx={{ textTransform: 'none', fontWeight: 700, border: '1px solid #cbd5e1', borderRadius: '10px', color: '#0f172a' }}>
                  {preview ? 'Change photo' : 'Upload photo'}
                  <input hidden type="file" accept="image/*" onChange={pickFile} />
                </Button>
                <Typography sx={{ fontFamily: FONT, fontSize: 12, color: '#94a3b8', mt: 0.75 }}>JPG or PNG, under 5 MB.</Typography>
              </Box>
            </Box>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField size="small" label="First name *" value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} error={!!errors.firstName} helperText={errors.firstName} />
              <TextField size="small" label="Last name *" value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} error={!!errors.lastName} helperText={errors.lastName} />
              <TextField size="small" label="Email *" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} error={!!errors.email} helperText={errors.email} />
              <TextField size="small" label="Phone" value={form.phoneNumber} onChange={(e) => setForm({ ...form, phoneNumber: e.target.value })} error={!!errors.phoneNumber} helperText={errors.phoneNumber || '10 digits'} inputProps={{ maxLength: 12 }} />
            </Box>
            {formError && <Alert severity="error">{formError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setEditOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={saving} sx={yellowBtn}>{saving ? 'Saving…' : 'Save changes'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* password */}
      <Dialog open={pwOpen} onClose={() => !pwSaving && setPwOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '18px', background: '#f8fafc' } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', px: 3, py: 2.25, borderBottom: '3px solid #6366f1', background: '#fff' }}>
          <Typography sx={{ flex: 1, fontFamily: FONT, fontWeight: 800, fontSize: 20 }}>Change password</Typography>
          <IconButton onClick={() => setPwOpen(false)} disabled={pwSaving} size="small"><MdClose size={20} /></IconButton>
        </Box>
        <Box component="form" onSubmit={savePw} noValidate sx={{ display: 'contents' }}>
          <DialogContent sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField size="small" label="Current password" type={show.old ? 'text' : 'password'} value={pw.oldPassword} onChange={(e) => setPw({ ...pw, oldPassword: e.target.value })} error={!!pwErrors.oldPassword} helperText={pwErrors.oldPassword} InputProps={eye('old')} autoComplete="current-password" />
            <Box>
              <TextField fullWidth size="small" label="New password" type={show.next ? 'text' : 'password'} value={pw.newPassword} onChange={(e) => setPw({ ...pw, newPassword: e.target.value })} error={!!pwErrors.newPassword} helperText={pwErrors.newPassword || 'At least 8 characters'} InputProps={eye('next')} autoComplete="new-password" />
              {pw.newPassword && (
                <Box sx={{ mt: 1 }}>
                  <LinearProgress variant="determinate" value={(pwScore / 4) * 100} sx={{ height: 6, borderRadius: 3, background: '#e2e8f0', '& .MuiLinearProgress-bar': { background: STRENGTH[pwScore][1] } }} />
                  <Typography sx={{ fontFamily: FONT, fontSize: 12, color: STRENGTH[pwScore][1], fontWeight: 700, mt: 0.5 }}>{STRENGTH[pwScore][0]}</Typography>
                </Box>
              )}
            </Box>
            <TextField size="small" label="Confirm new password" type={show.confirm ? 'text' : 'password'} value={pw.confirmPassword} onChange={(e) => setPw({ ...pw, confirmPassword: e.target.value })} error={!!pwErrors.confirmPassword} helperText={pwErrors.confirmPassword} InputProps={eye('confirm')} autoComplete="new-password" />
            {pwError && <Alert severity="error">{pwError}</Alert>}
          </DialogContent>
          <DialogActions sx={{ px: 3, py: 2, background: '#fff', borderTop: '1px solid #e2e8f0' }}>
            <Button onClick={() => setPwOpen(false)} disabled={pwSaving} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
            <Button type="submit" variant="contained" disabled={pwSaving} sx={yellowBtn}>{pwSaving ? 'Updating…' : 'Update password'}</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* revoke confirm */}
      <Dialog open={!!revokeTarget} onClose={() => !revoking && setRevokeTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '16px' } }}>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ fontFamily: FONT, fontWeight: 800, fontSize: 18, mb: 1 }}>{revokeTarget === 'others' ? 'Sign out other devices?' : revokeTarget?.current ? 'Sign out of this device?' : 'Sign out this device?'}</Typography>
          <Typography sx={{ fontFamily: FONT, fontSize: 14, color: '#475569' }}>
            {revokeTarget === 'others' ? 'Every device except this one will be signed out.' : revokeTarget?.current ? 'You will be signed out here and taken to the login page.' : `${browser(revokeTarget?.userAgent)} will be signed out.`}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRevokeTarget(null)} disabled={revoking} sx={{ textTransform: 'none', fontWeight: 700, color: '#475569' }}>Cancel</Button>
          <Button onClick={confirmRevoke} disabled={revoking} variant="contained" sx={{ textTransform: 'none', fontWeight: 800, background: '#dc2626', '&:hover': { background: '#b91c1c' } }}>{revoking ? 'Working…' : 'Sign out'}</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Profile;
